import { useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'
import type {
  BasketItem,
  LabourItem,
  CalculatorDictionary,
  CalculatorMetadata,
} from '@entities/calculator'
import { CALC_EVENTS, useTrackAction } from './analytics'
import LabourBlock from './LabourBlock'
import ProductEditorModal from './ProductEditorModal'
import { plural } from './plural'
import { writeErrorText } from './writeErrorText'
import { useOverlayBack } from './overlayBack'
import {
  buildLabourCost,
  buildRecipeCost,
  isPaywallStatus,
  costPerKg,
  costPerPiece,
  formatAmount,
  formatRub,
  parseInput,
  formDimensions,
  formLabel,
  indexBasket,
  indexDictionary,
  parseDecimal,
  quantShort,
  useCalculatorRecipeQuery,
  useLabourRatesQuery,
  useRecipeLabourQuery,
  useWriteBasketItemMutation,
} from '@entities/calculator'

interface RecipeCostSheetProps {
  cardId: number
  title: string
  dictionary: CalculatorDictionary | undefined
  metadata: CalculatorMetadata | undefined
  basketItems: readonly BasketItem[]
  onClose: () => void
  /** Уводит на вкладку ставок: без ставки часы ничего не добавляют. */
  onGoToRates: () => void
}

/**
 * Себестоимость одного рецепта: количество из состава × цена за единицу из
 * корзины. Считаем на клиенте, бэкенд состав только хранит.
 */
const RecipeCostSheet = ({
  cardId,
  title,
  dictionary,
  metadata,
  basketItems,
  onClose,
  onGoToRates,
}: RecipeCostSheetProps) => {
  useOverlayBack(onClose)
  const { recipe, isLoading, isError, error, isUnpublished } =
    useCalculatorRecipeQuery(cardId)
  // 403 здесь значит не «сбой», а закончившуюся подписку: раздел платный.
  const isPaywalled = isPaywallStatus(error?.response?.status)

  // Какому продукту сейчас вписывают цену. Уводить на другую вкладку за этим
  // не нужно: человек теряет рецепт из глаз и забывает, зачем шёл.
  const [pricingId, setPricingId] = useState<number | null>(null)
  const [isPriceSaved, setPriceSaved] = useState(false)
  const write = useWriteBasketItemMutation()
  const track = useTrackAction()

  // Сколько нужно получить. Пустое поле — считаем как в рецепте.
  const [target, setTarget] = useState('')

  const formWeight = recipe?.form?.weight ?? null
  const baseWeight = formWeight !== null && formWeight > 0 ? formWeight : null
  const basePieces =
    recipe?.pieces !== null && recipe?.pieces !== undefined && recipe.pieces > 0
      ? recipe.pieces
      : null

  // Состав дан на весь рецепт — либо на столько штук, либо на столько граммов.
  // Когда заполнено и то и другое, считаем в штуках: так договорились с бэком,
  // вес формы у штучных изделий справочный.
  const base = basePieces ?? baseWeight
  // В штуках пересчитываем только на целое: половины синнабона не бывает, а
  // дробный ввод рассыпал бы цену штуки — она обязана не зависеть от того, на
  // сколько изделий считают. Ввод меньше половины изделия округлится в ноль и
  // отсюда же будет отброшен как «пересчёта нет».
  const entered = parseInput(target)
  const wanted =
    entered !== null && basePieces !== null ? Math.round(entered) : entered
  const coefficient =
    base !== null && wanted !== null && wanted > 0 ? wanted / base : 1

  const cost = useMemo(() => {
    if (!recipe) return null
    return buildRecipeCost(
      recipe,
      indexDictionary(dictionary),
      indexBasket(basketItems),
      coefficient
    )
  }, [recipe, dictionary, basketItems, coefficient])

  const pricingIngredient = useMemo(
    () =>
      pricingId === null
        ? undefined
        : dictionary?.ingredients.find(
            item => item.ingredient_id === pricingId
          ),
    [dictionary, pricingId]
  )

  // Полноту расчёта отправляем один раз за открытие: дальше состав
  // пересчитывается на каждую введённую цену, и событий стало бы столько же.
  const isCostReported = useRef(false)
  useEffect(() => {
    if (isCostReported.current || !cost || !recipe) return
    // Пустой состав считать нечем — это не «неполный расчёт».
    if (recipe.blocks.length === 0) return
    isCostReported.current = true
    if (cost.missing.length === 0) {
      track(CALC_EVENTS.costComplete, String(cardId))
    } else {
      track(CALC_EVENTS.costIncomplete, `${cardId}__${cost.missing.length}`)
    }
  }, [cost, recipe, cardId, track])

  const closePricing = () => {
    setPricingId(null)
    setPriceSaved(false)
    write.reset()
  }

  const labourQuery = useRecipeLabourQuery(cardId)
  const ratesQuery = useLabourRatesQuery()

  const roles = dictionary?.labour_roles ?? []
  const rateByRole = useMemo(() => {
    const map = new Map<number, number>()
    ;(ratesQuery.data?.rates ?? []).forEach(rate => {
      const value = parseDecimal(rate.rate)
      if (value !== null) map.set(rate.role_id, value)
    })
    return map
  }, [ratesQuery.data])

  const savedLabour = useMemo(
    () => labourQuery.data?.items ?? [],
    [labourQuery.data]
  )
  // Пока часы набирают, считаем по набранному, а не по сохранённому.
  const [draftLabour, setDraftLabour] = useState<LabourItem[] | null>(null)
  const labourItems = draftLabour ?? savedLabour

  const labourCost = useMemo(
    () =>
      buildLabourCost(
        labourItems,
        new Map(roles.map(role => [role.role_id, role.name])),
        rateByRole
      ),
    [labourItems, roles, rateByRole]
  )

  // Итог складывается из двух сумм: продукты и работа.
  const totalKopecks = (cost?.totalKopecks ?? 0) + labourCost.totalKopecks
  const isPartial =
    (cost?.missing.length ?? 0) > 0 || labourCost.missing.length > 0

  const weight =
    baseWeight === null ? null : Math.round(baseWeight * coefficient)
  const perKg = cost ? costPerKg(totalKopecks, weight) : null
  // Выход после пересчёта: итог относится уже к нему, а не к рецептному.
  const pieces =
    basePieces === null ? null : Math.round(basePieces * coefficient)
  const perPiece = cost ? costPerPiece(totalKopecks, pieces) : null

  // Подпись над составом: на что рассчитаны показанные количества.
  const yieldLabel =
    pieces !== null
      ? `Расчёт на ${pieces} шт`
      : weight !== null
        ? `Расчёт на ${weight} г`
        : null

  return (
    <div className="cost-sheet" role="dialog" aria-modal="true">
      <div className="cost-sheet__head">
        <h2>{title}</h2>
        <button type="button" onClick={onClose} aria-label="Закрыть">
          <X size={20} />
        </button>
      </div>

      <div className="cost-sheet__body">
        {isLoading && <p className="cost-note">Загружаем состав…</p>}

        {isError && (
          <p className="cost-note warning">
            {isPaywalled
              ? 'Расчёт доступен по подписке — похоже, она закончилась.'
              : 'Не удалось получить состав рецепта. Попробуйте позже.'}
          </p>
        )}

        {isUnpublished && (
          <p className="cost-note warning">
            Калькулятор для этого рецепта сейчас недоступен.
          </p>
        )}

        {recipe && recipe.blocks.length === 0 && (
          <p className="cost-note">
            Состав ещё не заведён — посчитать себестоимость пока нечего.
          </p>
        )}

        {recipe?.form && (
          <div className="cost-form-info">
            <p className="cost-form-info__title">
              {formLabel(metadata, recipe.form.form_type)}
              {recipe.form.weight ? `, ${recipe.form.weight} г` : ''}
            </p>
            <p className="cost-form-info__dims">
              {formDimensions(metadata, recipe.form.form_type)
                .map(dimension => {
                  const raw = recipe.form?.[dimension.value] ?? null
                  const value = parseDecimal(raw)
                  return value === null
                    ? null
                    : `${dimension.label.replace(', см', '')} ${formatAmount(value)} см`
                })
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        )}

        {recipe && recipe.blocks.length > 0 && (
          <>
            {/* Без выхода пересчитывать не от чего: поле прячем целиком. */}
            {base !== null && (
              <label className="cost-field">
                <span>Рассчитать для, {basePieces !== null ? 'шт' : 'г'}</span>
                <input
                  type="text"
                  inputMode={basePieces !== null ? 'numeric' : 'decimal'}
                  value={target}
                  onChange={event => setTarget(event.target.value)}
                  placeholder={String(base)}
                />
              </label>
            )}

            {yieldLabel && <p className="cost-yield">{yieldLabel}</p>}

            {cost?.blocks.map(block => (
              <div key={block.blockId} className="cost-block">
                <h3>
                  {block.name ?? 'Без названия'}
                  <span>
                    {block.lines.every(line => line.costKopecks === null)
                      ? 'нет цен'
                      : formatRub(block.costKopecks)}
                  </span>
                </h3>

                {block.lines.map(line => (
                  <div key={line.ingredientId} className="cost-line">
                    <span className="cost-line__name">{line.label}</span>
                    <span className="cost-line__amount">
                      {formatAmount(line.amount)}
                      {line.quant ? ` ${quantShort(line.quant)}` : ''}
                    </span>
                    {line.costKopecks === null ? (
                      <button
                        type="button"
                        className="cost-line__add"
                        onClick={() => setPricingId(line.ingredientId)}
                        disabled={!dictionary}
                      >
                        указать цену
                      </button>
                    ) : (
                      <span className="cost-line__cost">
                        {formatRub(line.costKopecks)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {cost && cost.missing.length > 0 && (
              <div className="cost-note warning">
                <p>
                  Итог неполный: нет цен на {cost.missing.length}{' '}
                  {plural(
                    cost.missing.length,
                    'продукт',
                    'продукта',
                    'продуктов'
                  )}
                  . Впишите их прямо в строках состава — они отмечены.
                </p>
              </div>
            )}
            {roles.length > 0 && (
              <LabourBlock
                cardId={cardId}
                roles={roles}
                items={savedLabour}
                cost={labourCost}
                ratedRoleIds={new Set(rateByRole.keys())}
                isLoading={labourQuery.isLoading}
                onDraftChange={setDraftLabour}
                onGoToRates={onGoToRates}
              />
            )}

            <div className="cost-total">
              <div className="cost-total__row muted">
                <span>Продукты</span>
                <span>{formatRub(cost?.totalKopecks ?? 0)}</span>
              </div>

              {labourCost.lines.length > 0 && (
                <div className="cost-total__row muted">
                  <span>Работа</span>
                  <span>{formatRub(labourCost.totalKopecks)}</span>
                </div>
              )}

              <div className="cost-total__row">
                <span>Себестоимость{isPartial ? ' (неполная)' : ''}</span>
                <strong>
                  {isPartial ? 'от ' : ''}
                  {formatRub(totalKopecks)}
                </strong>
              </div>

              {/* Вес и цена килограмма — про изделие, которое считают по
                  форме. У штучного рецепта вес формы относится ко всей партии,
                  и обе строки читались бы как вес и цена одной штуки. */}
              {basePieces === null && weight !== null && (
                <div className="cost-total__row muted">
                  <span>Вес изделия</span>
                  <span>{weight} г</span>
                </div>
              )}

              {basePieces === null && perKg !== null && (
                <div className="cost-total__row muted">
                  <span>За килограмм</span>
                  <span>{formatRub(perKg)}</span>
                </div>
              )}

              {/* За штуку — только у штучных изделий: у торта выход в штуках
                  не задан, и делить итог не на что. */}
              {perPiece !== null && (
                <div className="cost-total__row">
                  <span>За штуку</span>
                  <strong>{formatRub(perPiece)}</strong>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {pricingIngredient && (
        // Вне прокручиваемого тела: шторка цены накрывает рецепт целиком, а не
        // едет вместе с составом.
        <ProductEditorModal
          key={pricingIngredient.ingredient_id}
          ingredient={pricingIngredient}
          item={basketItems.find(
            item => item.ingredient_id === pricingIngredient.ingredient_id
          )}
          isSaving={write.isLoading}
          error={writeErrorText(write.error ?? null)}
          closeRequested={isPriceSaved}
          onSubmit={(amount, price) =>
            write.mutate(
              {
                ingredient_id: pricingIngredient.ingredient_id,
                amount,
                price,
                exists: basketItems.some(
                  item => item.ingredient_id === pricingIngredient.ingredient_id
                ),
              },
              // Корзина правится в кеше, а она приходит сюда пропом — состав
              // пересчитается сам, рецепт закрывать не нужно.
              {
                onSuccess: () => {
                  track(
                    CALC_EVENTS.addPriceFromRecipe,
                    String(pricingIngredient.ingredient_id)
                  )
                  setPriceSaved(true)
                },
              }
            )
          }
          onClose={closePricing}
        />
      )}
    </div>
  )
}

export default RecipeCostSheet
